import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import CommonMenu from 'src/components/CommonMenu';
import { useNavigate } from 'react-router';
import { isBefore } from 'date-fns';
import { Poppins, DateCapsule, TextOverflowTooltip } from 'src/common';
import { AssessmentModal, ConfirmDeleteModal } from '../../../components/modals';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import { IncompleteStateMarker } from 'src/common/IncompleteStateMarker';
import { Tile, TileDescription, TileDescriptionContainer, TileTitle } from 'src/components/Tile';
import { Link } from 'react-router-dom';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { ScenarioSvg } from 'src/assets/dynamicSvg';
import { AssessmentsQuery, CompletionState, useDeleteAssessmentMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import InfoHint from 'src/components/InfoHint';
import { LABELS } from 'src/components/labels';

const Container = styled(Tile)<{ $isSelected: boolean; $disabled?: boolean }>`
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.gp2};
    `};

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `};
`;

const BottomContainer = styled.div`
  display: flex;
`;

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  height: 28px;
`;

interface AssessmentCardProps {
  assessment: AssessmentsQuery['assessments'][number];
  isSelected: boolean;
  compareMode: boolean;
  onSelect: (val: string) => void;
  disabled?: boolean;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({
  assessment,
  isSelected,
  compareMode,
  onSelect,
  disabled,
}) => {
  const navigate = useNavigate();
  const [openAssessmentId, setOpenAssessmentId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { refetchQuery } = useCache();

  const handleClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const [remove] = useDeleteAssessmentMutation({
    variables: {
      id: assessment.id,
    },
    onCompleted: () => {
      setIsDeleteModalOpen(false);
      refetchQuery({ include: ['assessments'] });
      handleClose();
    },
  });

  const handleOnEditClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    e.stopPropagation();
    handleClose();
    setOpenAssessmentId(assessment.id);
  };
  const handleOnViewClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    e.stopPropagation();
    navigate(`/risk-assessments/${assessment.id}`);
  };

  const handleOnDeleteClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    e.stopPropagation();
    remove();
  };

  const openDeleteModal = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    e.stopPropagation();
    handleClose();
    setIsDeleteModalOpen(true);
  };

  const hasIncompleteTech = assessment.scenarios?.some(
    (el) => el?.isIncomplete === CompletionState.Incomplete || el?.isIncomplete === CompletionState.IncompleteEmptyVar,
  );

  return (
    <>
      {openAssessmentId && <AssessmentModal id={openAssessmentId} onClose={() => setOpenAssessmentId(null)} />}
      <Container
        className="hover-shadow"
        $isSelected={isSelected}
        $disabled={disabled}
        onClick={() => {
          if (compareMode) {
            onSelect(assessment.id);
          } else {
            if (!anchorEl) {
              navigate(`/risk-assessments/${assessment.id}`);
              mpEvent(MPEvents.Link, {
                source: { value: ['Assessments'] },
                destination: { value: ['Assessment'], params: { id: assessment.id } },
                tags: ['ASSESSMENT'],
              });
            }
          }
        }}
      >
        <CommonMenu
          onEditClick={handleOnEditClick}
          onViewClick={handleOnViewClick}
          onDeleteClick={openDeleteModal}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        />
        {isDeleteModalOpen && (
          <ConfirmDeleteModal onConfirm={handleOnDeleteClick} onClose={() => setIsDeleteModalOpen(false)} />
        )}
        <div css="display: flex;justify-content: space-between;align-items: space-between;">
          <TextOverflowTooltip tooltipText={assessment.name}>
            {compareMode ? (
              <TileTitle data-cy="assessment-card-title">{assessment.name}</TileTitle>
            ) : (
              <Link
                to={`/risk-assessments/${assessment.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  mpEvent(MPEvents.Link, {
                    source: { value: ['Assessments'] },
                    destination: { value: ['Assessment'], params: { id: assessment.id } },
                    tags: ['ASSESSMENT'],
                  });
                }}
              >
                <TileTitle data-cy="assessment-card-title">{assessment.name}</TileTitle>
              </Link>
            )}
          </TextOverflowTooltip>
          <div css="display: flex;align-items: center;">
            {hasIncompleteTech && (
              <div css="margin-right: 8px;margin-left: autopx;">
                <IncompleteStateMarker
                  initState="TECH"
                  titleTech="One or more scenarios are missing calculation data and have been excluded from the calculations."
                />
              </div>
            )}
            {!compareMode && (
              <ThreeDots aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} dataCy="tdm-card" />
            )}
          </div>
        </div>

        <TileDescriptionContainer>
          <TileDescription>
            <Poppins px={14} color="cflowerBlue">
              {assessment.description}
            </Poppins>
          </TileDescription>
          <div className="price">
            <InfoHint tooltipText={LABELS.riskLive} noIcon>
              <div className="price__live" />
            </InfoHint>
            <InfoHint tooltipText={LABELS.expectedLoss} noIcon>
              <Poppins px={22} weight={600} nowrap>
                {numeralFormat(assessment.AssessmentLiveRisk?.expectedLoss)}
              </Poppins>
            </InfoHint>
          </div>
        </TileDescriptionContainer>
        <BottomContainer>
          <div css="display: flex;align-items: center;flex: 1;">
            <ScenarioSvg />
            <div>
              <Poppins css="margin-left: 6px;" px={18} weight={500}>
                {assessment.scenarios?.length}
              </Poppins>
              <Poppins css="margin-left: 4px;" px={14} color="cflowerBlue">
                {assessment.scenarios?.length === 1 ? 'scenario' : 'scenarios'}
              </Poppins>
            </div>
          </div>
          <DateContainer>
            {assessment.reviewDate && (
              <>
                <Poppins weight={500} px={14} color="cflowerBlue" nowrap css="margin-right: 10px">
                  Review Date
                </Poppins>
                <DateCapsule $highlighted={isBefore(new Date(assessment.reviewDate), new Date())}>
                  <Poppins weight={600} px={14}>
                    {formatDate(assessment.reviewDate)}
                  </Poppins>
                </DateCapsule>
              </>
            )}
          </DateContainer>
        </BottomContainer>
      </Container>
    </>
  );
};
